import { useEffect, useRef } from "react";

/**
 * AdPortal — moves a pre-existing GAM ad div from the body into this
 * component's position in the React tree. Used for DESKTOP slots only.
 *
 * The body div starts with display:none so it takes no visual space.
 * When this component mounts, it moves the div here and sets display:"".
 * On unmount, it moves the div back and hides it again.
 */
export default function AdPortal({
  slotId,
  className = "",
  style,
}: {
  slotId: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const adDiv = document.getElementById(slotId);
    const container = containerRef.current;
    if (!adDiv || !container) return;

    // Move into layout and make visible
    adDiv.style.display = "";
    container.appendChild(adDiv);

    return () => {
      // Move back to body and hide
      adDiv.style.display = "none";
      document.body.appendChild(adDiv);
    };
  }, [slotId]);

  return <div ref={containerRef} className={className} style={style} />;
}

/**
 * GptAdSlot — renders a GAM ad div directly in the React tree and calls
 * googletag.display() on mount. Used for MOBILE-ONLY slots that have
 * their own div IDs (not shared with body divs).
 *
 * The slot must be defined in the head script via googletag.defineSlot().
 */
export function GptAdSlot({
  slotId,
  className = "",
  style,
}: {
  slotId: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const displayed = useRef(false);

  useEffect(() => {
    if (displayed.current) return;

    const w = window as any;
    if (!w.googletag?.cmd) return;

    w.googletag.cmd.push(() => {
      w.googletag.display(slotId);
      displayed.current = true;
    });
  }, [slotId]);

  return (
    <div className={className} style={style}>
      <div id={slotId} />
    </div>
  );
}
